import React, { useState } from 'react';
import { Box, Stack, Typography, Card } from '@mui/material';
import { styled } from '@mui/material/styles';
import { theme } from '../../constants/theme';
import { useThemeContext } from '../../hook/useThemeManager';
import { FONT_ENUM } from '../../types/global/fonts.types';
import CustomLazyLoadImage from '../Image/CustomLazyLoadImage';
import { influencerProfilePhotoCoverSx } from '../BuzzBridge/utils/creatorProfileHelpers';
import CustomButton from '../Button/CustomButton';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import CancelIcon from '@mui/icons-material/Cancel';
import MailIcon from '@mui/icons-material/Mail';
import FavoriteIcon from '@mui/icons-material/Favorite';
import CloseIcon from '@mui/icons-material/Close';
import { FilingStatusEnum } from '../../constants/types/api/application';
import { SpeedDial, SpeedDialAction, SpeedDialIcon } from '@mui/material';
import { ArchiveOutlined, TheaterComedyOutlined, MoreVert } from '@mui/icons-material';
import { ReusableDrawer } from '../ReusableDrawer/ReusableDrawer';
import { WorkProfile } from '../ScreenContainer/EsMyProfileContainer/WorkProfile';
import { IApplication } from '../../types/global/ListRender.types';
import { formatWithOrdinal } from '../../utils/AppUtils';
import { IWorkProfile } from '../../types/global/helper';
import { MessageDialogue } from '../MessageDialogue/MessageDialogue';
import {
    BRAND_LUXE_PRIMARY,
    BRAND_LUXE_SECONDARY,
    BRAND_OUTLINE_VARIANT,
    BRAND_SURFACE_CONTAINER_HIGHEST,
    BRAND_SURFACE_CONTAINER_LOW,
} from '../../pages/BrandDashboard/brandDashboardTokens';
import QuickMessageIcon from '../CustomSVGIcons/QuickMessageIcon';

interface IApplicationCardProps {
    application: IApplication;
    onViewDetailsClick: (application: IApplication) => void;
    /** When true, "View Details" only runs the callback (e.g. in-app navigation) and does not open the profile drawer. */
    skipProfileDrawerOnViewDetails?: boolean;
    onRejectClick?: (application: IApplication) => void;
    onAcceptClick?: (application: IApplication) => void;
    onMessageClick?: (application: IApplication) => void;
    onArchiveClick?: (application: IApplication) => void;
    onCastClick?: (application: IApplication) => void;
}

const StyledCard = styled(Card)(({ theme }) => ({
    height: '100%',
    display: 'flex',
    flexDirection: 'column',
    borderRadius: '18px',
    overflow: 'hidden',
    transition: 'transform 0.2s, border-color 0.2s',
    border: `1px solid ${BRAND_OUTLINE_VARIANT}`,
    '&:hover': {
        transform: 'translateY(-4px)',
        borderColor: 'rgba(255, 201, 101, 0.24)',
    },
}));

export const ApplicationCard: React.FC<IApplicationCardProps> = ({
    application,
    onViewDetailsClick,
    skipProfileDrawerOnViewDetails,
    onRejectClick,
    onAcceptClick,
    onMessageClick,
    onArchiveClick,
    onCastClick,
}) => {
    const { themeStyle } = useThemeContext();
    const [drawerOpen, setDrawerOpen] = useState(false);
    const [speedDialOpen, setSpeedDialOpen] = useState(false);
    const [messageDialogueOpen, setMessageDialogueOpen] = useState(false);

    // Get status icon based on status
    const getStatusIcon = (status: string) => {
        switch (status) {
            case FilingStatusEnum.YES:
                return (
                    <CheckCircleIcon
                        style={{ color: theme[themeStyle].tertiary_green, fontSize: '16px', marginRight: '4px' }}
                    />
                );
            case FilingStatusEnum.NO:
                return (
                    <CancelIcon
                        style={{ color: theme[themeStyle].primary_red, fontSize: '16px', marginRight: '4px' }}
                    />
                );
            case FilingStatusEnum.INVITED:
                return (
                    <MailIcon
                        style={{ color: theme[themeStyle].primary_orange, fontSize: '16px', marginRight: '4px' }}
                    />
                );
            default:
                return null;
        }
    };

    // Update message click handler
    const handleMessageClick = () => {
        setMessageDialogueOpen(true);
        if (onMessageClick) onMessageClick(application);
    };

    // Speed dial actions
    const actions = [
        {
            icon: <QuickMessageIcon width={16} height={16} stroke="currentColor" />,
            name: 'Message',
            onClick: () => {
                handleMessageClick();
                setSpeedDialOpen(false);
            },
        },
        {
            icon: <ArchiveOutlined />,
            name: 'Archive',
            onClick: () => {
                if (onArchiveClick) onArchiveClick(application);
                setSpeedDialOpen(false);
            },
        },
        {
            icon: <TheaterComedyOutlined />,
            name: 'Cast',
            onClick: () => {
                if (onCastClick) onCastClick(application);
                setSpeedDialOpen(false);
            },
        },
    ];

    // Handle view details click
    const handleViewDetailsClick = () => {
        if (!skipProfileDrawerOnViewDetails) {
            setDrawerOpen(true);
        }
        if (onViewDetailsClick) onViewDetailsClick(application);
    };

    return (
        <>
            <StyledCard sx={{ backgroundColor: BRAND_SURFACE_CONTAINER_LOW }}>
                <Stack
                    direction="column"
                    spacing={2}
                    alignItems="flex-start"
                    position="relative"
                    sx={{ position: 'relative' }}>
                    <Box sx={{ position: 'relative', width: '100%' }}>
                        {application?.profile?.profile_picture ? (
                            <CustomLazyLoadImage
                                src={application.profile.profile_picture}
                                alt="user profile"
                                style={{
                                    width: '100%',
                                    height: '250px',
                                    display: 'block',
                                    ...influencerProfilePhotoCoverSx,
                                }}
                            />
                        ) : (
                            <Box
                                sx={{
                                    width: '100%',
                                    height: '250px',
                                    background:
                                        'linear-gradient(160deg, rgba(38,38,38,1) 0%, rgba(19,19,19,1) 60%, rgba(32,31,31,1) 100%)',
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                }}>
                                <Typography
                                    sx={{
                                        color: 'rgba(255,255,255,0.45)',
                                        fontFamily: FONT_ENUM.MAIN_BOLD,
                                        fontSize: '0.82rem',
                                        textTransform: 'uppercase',
                                        letterSpacing: '0.08em',
                                    }}>
                                    No preview image
                                </Typography>
                            </Box>
                        )}
                        <Box
                            sx={{
                                position: 'absolute',
                                top: 16,
                                right: 16,
                                px: 1.5,
                                py: 0.5,
                                borderRadius: 999,
                                backdropFilter: 'blur(12px)',
                                backgroundColor: 'rgba(0,0,0,0.4)',
                                border: '1px solid rgba(0,253,193,0.25)',
                            }}>
                            <Typography
                                sx={{
                                    color: BRAND_LUXE_SECONDARY,
                                    fontFamily: FONT_ENUM.MAIN_BOLD,
                                    fontSize: '10px',
                                    textTransform: 'uppercase',
                                    letterSpacing: '0.12em',
                                }}>
                                {application.percentage_match}% Match
                            </Typography>
                        </Box>
                        <Box sx={{ position: 'absolute', bottom: 0, right: 10, zIndex: 2 }}>
                            <SpeedDial
                                ariaLabel="Applicant Actions"
                                icon={<SpeedDialIcon openIcon={<MoreVert />} />}
                                onClose={() => setSpeedDialOpen(false)}
                                onOpen={() => setSpeedDialOpen(true)}
                                open={speedDialOpen}
                                direction="left"
                                sx={{
                                    '& .MuiFab-primary': {
                                        width: 36,
                                        height: 36,
                                        minHeight: 36,
                                        backgroundColor: BRAND_SURFACE_CONTAINER_HIGHEST,
                                        color: '#fff',
                                        border: `1px solid ${BRAND_OUTLINE_VARIANT}`,
                                        '&:hover': {
                                            color: '#fff',
                                            backgroundColor: BRAND_SURFACE_CONTAINER_HIGHEST,
                                        },
                                    },
                                }}>
                                {actions.map(action => (
                                    <SpeedDialAction
                                        key={action.name}
                                        icon={action.icon}
                                        tooltipTitle={action.name}
                                        onClick={action.onClick}
                                        sx={{
                                            backgroundColor: BRAND_SURFACE_CONTAINER_HIGHEST,
                                            color: '#fff',
                                            '&:hover': {
                                                backgroundColor: '#2f2f2f',
                                                color: '#fff',
                                            },
                                        }}
                                    />
                                ))}
                            </SpeedDial>
                        </Box>
                    </Box>
                    <Box p={2}>
                        <Typography
                            sx={{
                                color: '#fff',
                                fontFamily: FONT_ENUM.MAIN_BOLD,
                                fontSize: '2rem',
                                letterSpacing: '-0.03em',
                                lineHeight: 1.05,
                                textTransform: 'uppercase',
                            }}>
                            {`${application.user?.firstName} ${application.user?.lastName}`}
                        </Typography>
                        <Box sx={{ mt: 1, display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
                                <Typography
                                    sx={{
                                        fontFamily: FONT_ENUM.MAIN_BOLD,
                                        fontSize: '0.67rem',
                                        color: BRAND_LUXE_PRIMARY,
                                        textTransform: 'uppercase',
                                        letterSpacing: '0.14em',
                                    }}>
                                    Role:
                                </Typography>
                                <Typography
                                    sx={{
                                        fontFamily: FONT_ENUM.MAIN_MEDIUM,
                                        fontSize: '0.95rem',
                                        color: '#fff',
                                    }}>
                                    {application.role?.character || 'General'}
                                </Typography>
                            </Box>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
                                <Typography
                                    sx={{
                                        fontFamily: FONT_ENUM.MAIN_BOLD,
                                        fontSize: '0.67rem',
                                        color: BRAND_LUXE_PRIMARY,
                                        textTransform: 'uppercase',
                                        letterSpacing: '0.14em',
                                    }}>
                                    Applied:
                                </Typography>
                                <Typography
                                    sx={{
                                        color: 'rgba(255,255,255,0.72)',
                                        fontFamily: FONT_ENUM.MAIN_MEDIUM,
                                        fontSize: '0.86rem',
                                    }}>
                                    {formatWithOrdinal(application.created_at)}
                                </Typography>
                            </Box>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
                                <Typography
                                    sx={{
                                        fontFamily: FONT_ENUM.MAIN_BOLD,
                                        fontSize: '0.67rem',
                                        color: BRAND_LUXE_PRIMARY,
                                        textTransform: 'uppercase',
                                        letterSpacing: '0.14em',
                                    }}>
                                    Status:
                                </Typography>
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                    {getStatusIcon(application.filing_status || 'unfiled')}
                                    <Typography
                                        sx={{
                                            color: '#fff',
                                            fontFamily: FONT_ENUM.MAIN_MEDIUM,
                                            fontSize: '0.95rem',
                                            textTransform: 'capitalize',
                                        }}>
                                        {application.filing_status || 'unfiled'}
                                    </Typography>
                                </Box>
                            </Box>
                        </Box>
                    </Box>
                </Stack>
                <Box sx={{ p: 2, display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 1.5 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <CustomButton
                            label=""
                            withIcon={true}
                            beginningIcon={<CloseIcon />}
                            onClick={() => onRejectClick && onRejectClick(application)}
                            buttonProps={{ variant: 'outlined' }}
                            sx={{
                                borderColor: 'rgba(72,72,71,0.35)',
                                color: '#ff7351',
                                minWidth: '44px',
                                width: '44px',
                                height: '44px',
                                borderRadius: '12px',
                                backgroundColor: 'rgba(255,255,255,0.05)',
                                '&:hover': {
                                    borderColor: 'rgba(72,72,71,0.35)',
                                    backgroundColor: 'rgba(255,255,255,0.10)',
                                },
                            }}
                        />
                        <CustomButton
                            label=""
                            withIcon={true}
                            beginningIcon={<FavoriteIcon />}
                            onClick={() => onAcceptClick && onAcceptClick(application)}
                            buttonProps={{ variant: 'outlined' }}
                            sx={{
                                borderColor: 'rgba(72,72,71,0.35)',
                                color: BRAND_LUXE_SECONDARY,
                                minWidth: '44px',
                                width: '44px',
                                height: '44px',
                                borderRadius: '12px',
                                backgroundColor: 'rgba(255,255,255,0.05)',
                                '&:hover': {
                                    borderColor: 'rgba(72,72,71,0.35)',
                                    backgroundColor: 'rgba(255,255,255,0.10)',
                                },
                            }}
                        />
                        <CustomButton
                            label=""
                            withIcon={true}
                            beginningIcon={<QuickMessageIcon width={18} height={18} stroke="currentColor" />}
                            onClick={handleMessageClick}
                            buttonProps={{ variant: 'outlined' }}
                            sx={{
                                borderColor: 'rgba(72,72,71,0.35)',
                                color: BRAND_LUXE_PRIMARY,
                                minWidth: '44px',
                                width: '44px',
                                height: '44px',
                                borderRadius: '12px',
                                backgroundColor: 'rgba(255,255,255,0.05)',
                                '&:hover': {
                                    borderColor: 'rgba(72,72,71,0.35)',
                                    backgroundColor: 'rgba(255,255,255,0.10)',
                                },
                            }}
                        />
                    </Box>
                    <CustomButton
                        label="View Details"
                        onClick={handleViewDetailsClick}
                        customStyle={{
                            borderRadius: 6,
                        }}
                        sx={{
                            flex: 1,
                            height: '44px',
                            background: `linear-gradient(to top right, ${BRAND_LUXE_PRIMARY}, #feb700)`,
                            color: '#392700',
                            border: 'none',
                            textTransform: 'uppercase',
                            letterSpacing: '0.08em',
                            fontSize: '0.78rem',
                            boxShadow: '0 0 0 rgba(0,0,0,0)',
                            '&:hover': {
                                background: `linear-gradient(to top right, ${BRAND_LUXE_PRIMARY}, #feb700)`,
                                boxShadow: '0 0 20px rgba(255,201,101,0.35)',
                                filter: 'brightness(1.05)',
                            },
                        }}
                    />
                </Box>
            </StyledCard>

            {/* Drawer for profile details */}
            <ReusableDrawer
                open={drawerOpen}
                onClose={() => setDrawerOpen(false)}
                content={
                    <WorkProfile
                        workProfile={[application.profile as unknown as IWorkProfile]}
                        external={true}
                        isPreviewMode
                        applicationData={application}
                        onAcceptApplication={() => onAcceptClick && onAcceptClick(application)}
                        onRejectApplication={() => onRejectClick && onRejectClick(application)}
                        onMessageApplication={handleMessageClick}
                    />
                }
                anchor={'right'}
            />

            {/* Message Dialogue */}
            <MessageDialogue
                open={messageDialogueOpen}
                onClose={() => setMessageDialogueOpen(false)}
                recipient={application.user}
                roleId={application.role._id}
                auditionId={application.audition}
            />
        </>
    );
};
